import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Send, Shield, CheckCircle } from 'lucide-react';
import httpClient from '../../Api/axios';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [shake, setShake] = useState(false);
  const [timer, setTimer] = useState(0);

  // Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShake(false);

    if (!email.trim()) {
      setError('Please enter your email address');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    setLoading(true);

    try {
      // ✅ FIXED: Added /api prefix
      const response = await httpClient.post('/api/admin/send-otp', { email });
      
      if (response.data.success) {
        setSuccess(`OTP sent to ${email}`);
        setStep(2);
        setTimer(60);
        
        const interval = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setError(error.response?.data?.message || 'Failed to send OTP');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setShake(false);

    if (!otp.trim()) {
      setError('Please enter the OTP');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    setLoading(true);

    try {
      // ✅ FIXED: Added /api prefix
      const response = await httpClient.post('/api/admin/verify-otp', { email, otp });
      
      if (response.data.success) {
        localStorage.setItem('isPasswordResetVerified', 'true');
        localStorage.setItem('resetEmail', email);
        
        setSuccess('OTP verified successfully!');
        
        setTimeout(() => {
          navigate('/admin/reset-password', { 
            state: { email, isVerified: true } 
          });
        }, 500);
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setError(error.response?.data?.message || 'Invalid OTP');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (timer > 0) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // ✅ FIXED: Added /api prefix
      const response = await httpClient.post('/api/admin/resend-otp', { email });
      
      if (response.data.success) {
        setSuccess('New OTP sent successfully');
        setTimer(60);
        
        const interval = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Go back to login
  const handleBackToLogin = () => {
    localStorage.removeItem('isPasswordResetVerified');
    localStorage.removeItem('resetEmail');
    navigate('/admin');
  };

  return (
    <div className="forgot-password-container">
      <div className="login-bg">
        <div className="bg-blob blob-1"></div>
        <div className="bg-blob blob-2"></div>
        <div className="bg-blob blob-3"></div>
      </div>

      <div className={`forgot-card ${shake ? 'shake' : ''}`}>
        <button className="back-btn" onClick={handleBackToLogin}>
          <ArrowLeft size={20} />
          Back to Login
        </button>

        <div className="forgot-header">
          <div className="forgot-logo">
            <Shield size={40} className="logo-icon" />
            <span className="logo-text">Reset Password</span>
          </div>
          
          {step === 1 ? (
            <>
              <h2 className="forgot-title">Forgot Password?</h2>
              <p className="forgot-subtitle">
                Enter your email address and we'll send you an OTP to reset your password
              </p>
            </>
          ) : (
            <>
              <h2 className="forgot-title">Verify OTP</h2>
              <p className="forgot-subtitle">
                We've sent a 6-digit OTP to <strong>{email}</strong>
              </p>
            </>
          )}
        </div>

        {step === 1 ? (
          <form onSubmit={handleSendOTP} className="forgot-form">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  className="form-input"
                  placeholder="Enter your registered email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="success-message">
                <CheckCircle size={18} />
                <span>{success}</span>
              </div>
            )}

            <button
              type="submit"
              className="send-otp-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Sending OTP...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send OTP
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="forgot-form">
            <div className="form-group">
              <label className="form-label">Enter OTP</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-input otp-input"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setOtp(value);
                    }
                  }}
                  maxLength={6}
                  disabled={loading}
                />
              </div>
              <div className="otp-timer">
                {timer > 0 ? (
                  <span>Resend OTP in {timer}s</span>
                ) : (
                  <button
                    type="button"
                    className="resend-btn"
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="success-message">
                <CheckCircle size={18} />
                <span>{success}</span>
              </div>
            )}

            <button
              type="submit"
              className="verify-otp-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify OTP'
              )}
            </button>
          </form>
        )}

        <div className="demo-info">
          <p className="demo-text">
            <span className="demo-label">Registered Email:</span>
            <span className="demo-value">shivanikharade18@gmail.com</span>
          </p>
          <p className="demo-hint">
            OTP will be sent to your registered email address
          </p>
        </div>
      </div>

      <style jsx>{`
        .forgot-password-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f0c29, #302b63, #24243e);
          position: relative;
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
        }

        .login-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 0;
        }

        .bg-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.3;
          animation: float 20s ease-in-out infinite;
        }

        .blob-1 {
          width: 500px;
          height: 500px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          top: -150px;
          right: -100px;
          animation-delay: 0s;
        }

        .blob-2 {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #ec4899, #8b5cf6);
          bottom: -100px;
          left: -100px;
          animation-delay: -5s;
        }

        .blob-3 {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #6366f1, #3b82f6);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(50px, -30px) scale(1.1);
          }
          50% {
            transform: translate(-20px, 40px) scale(0.9);
          }
          75% {
            transform: translate(30px, -20px) scale(1.05);
          }
        }

        .forgot-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 48px 40px;
          width: 100%;
          max-width: 420px;
          position: relative;
          z-index: 1;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
          animation: slideUp 0.6s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .forgot-card.shake {
          animation: shake 0.5s ease-in-out;
        }

        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-8px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(8px);
          }
        }

        .back-btn {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          padding: 8px 0;
          margin-bottom: 16px;
        }

        .back-btn:hover {
          color: #8b5cf6;
        }

        .forgot-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .forgot-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .logo-icon {
          color: #8b5cf6;
          filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.3));
        }

        .logo-text {
          font-size: 22px;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff, #a0aec0);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .forgot-title {
          font-size: 26px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 8px;
        }

        .forgot-subtitle {
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .forgot-subtitle strong {
          color: #8b5cf6;
        }

        .forgot-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-label {
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
          font-weight: 500;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          color: rgba(255, 255, 255, 0.3);
          pointer-events: none;
        }

        .form-input {
          width: 100%;
          padding: 14px 14px 14px 46px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: #ffffff;
          font-size: 14px;
          transition: all 0.3s ease;
          outline: none;
        }

        .form-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }

        .form-input:focus {
          border-color: #8b5cf6;
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
        }

        .form-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .otp-input {
          text-align: center;
          font-size: 20px;
          letter-spacing: 8px;
          padding: 14px;
          font-weight: 600;
        }

        .otp-timer {
          text-align: center;
          margin-top: 4px;
        }

        .otp-timer span {
          color: rgba(255, 255, 255, 0.4);
          font-size: 13px;
        }

        .resend-btn {
          background: none;
          border: none;
          color: #8b5cf6;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .resend-btn:hover:not(:disabled) {
          color: #a78bfa;
          text-decoration: underline;
        }

        .resend-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 10px;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          animation: fadeIn 0.3s ease;
        }

        .error-message span {
          color: #f87171;
          font-size: 13px;
          font-weight: 500;
        }

        .success-message {
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.2);
          border-radius: 10px;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeIn 0.3s ease;
        }

        .success-message span {
          color: #4ade80;
          font-size: 13px;
          font-weight: 500;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .send-otp-btn, .verify-otp-btn {
          padding: 14px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 12px;
          color: #ffffff;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .send-otp-btn:hover:not(:disabled), 
        .verify-otp-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(139, 92, 246, 0.3);
        }

        .send-otp-btn:disabled,
        .verify-otp-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2.5px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .demo-info {
          margin-top: 24px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .demo-text {
          text-align: center;
          font-size: 13px;
          margin-bottom: 6px;
        }

        .demo-label {
          color: rgba(255, 255, 255, 0.4);
          margin-right: 6px;
        }

        .demo-value {
          color: #8b5cf6;
          font-weight: 500;
        }

        .demo-hint {
          text-align: center;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.3);
          margin: 0;
        }

        @media (max-width: 480px) {
          .forgot-card {
            padding: 32px 24px;
          }
          .forgot-title {
            font-size: 22px;
          }
          .logo-text {
            font-size: 18px;
          }
          .form-input {
            padding: 12px 12px 12px 42px;
            font-size: 13px;
          }
          .otp-input {
            font-size: 18px;
            letter-spacing: 6px;
          }
          .send-otp-btn,
          .verify-otp-btn {
            padding: 12px;
            font-size: 14px;
          }
          .bg-blob {
            opacity: 0.2;
          }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;